import classNames from "classnames";
import React, { useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Filters,
  isValidEntityType,
  Position,
  Query,
  QueryPanelLogic,
  RANGE_FILTERS,
} from "types";
import {
  getFilterIcon,
  getOperatorTitle,
  isSignOperator,
} from "helpers/utility/queryFilterHelpers";
import { getMislabelsThresholdRange } from "redux/SingleDataset/selectors";
import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import { getFalseRangeDisplayValue } from "helpers/utility/utilities";
import { ENTITY_TYPE_PARAMETER } from "helpers/constants/miscellaneous";
import AddFilter from "views/components/QueryPanel/AddFilter";
import QueryTab from "views/components/QueryTab";
import useQueryParam from "hooks/useQueryParam";
import useVqlParams from "hooks/useVqlParams";
import { useVisualSimilarityImages } from "hooks/useVisualSimilarityImages";
import styles from "./style.module.scss";
import { formatDateWithHours } from "helpers/utility/formatters";
import { Dropdown, Button } from "antd";
import {
  LABEL_OUTLIER_QUERY_MIN_VALUE,
  RANGE_QUERY_MIN_VALUE,
} from "../../../helpers/constants/queries";

interface QueryPanelRowProps {
  type?: QueryPanelLogic;
  queries: Query[];
  onReset: () => void;
  onRemove: (q: Query) => void;
  onClick: (q: Query, p: Position) => void;
  interactive?: boolean;
  onAdvancedClick?: () => void;
  hideAddFilter?: boolean;
  dropZoneId?: string;
}

export const QueryPanelRow = ({
  queries,
  type = QueryPanelLogic.AND,
  onReset,
  onClick,
  onRemove,
  interactive = true,
  onAdvancedClick,
  hideAddFilter = false,
}: QueryPanelRowProps) => {
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeFromQueryParam = isValidEntityType(entityParam)
    ? entityParam
    : null;
  const mislabelsRange = useSelector((state) =>
    getMislabelsThresholdRange(state, entityTypeFromQueryParam),
  );
  const rowRef = useRef<HTMLDivElement>(null);

  const logicMap: Record<QueryPanelLogic, string> = {
    [QueryPanelLogic.EXCLUDE]: "Exclude",
    [QueryPanelLogic.OR]: "OR",
    [QueryPanelLogic.AND]: "",
  };

  const logicText = logicMap[type] || "";

  const getTabLabels = (query: Query): string[] => {
    const { type, values } = query;

    const showFalseValue = [
      Filters.ISSUES,
      Filters.OUTLIERS,
      Filters.LABEL_OUTLIER,
    ].includes(type as Filters);

    if (showFalseValue && values.length === 2) {
      return values.map((v) =>
        getFalseRangeDisplayValue(
          parseFloat(v),
          type === Filters.LABEL_OUTLIER
            ? LABEL_OUTLIER_QUERY_MIN_VALUE
            : RANGE_QUERY_MIN_VALUE,
        ).toString(),
      );
    }

    // Handle datetime formatting for both CREATED_AT and custom metadata datetime fields
    const isDatetimeFilter = query.type === Filters.CREATED_AT || false;

    if (isDatetimeFilter) {
      if (values.length === 1) {
        return [formatDateWithHours(values[0])];
      } else if (values.length === 2) {
        return [formatDateWithHours(values[0]), formatDateWithHours(values[1])];
      }
    }

    return values;
  };

  const checkIsClickAllowed = (query: Query): boolean => {
    //if thresholds donot exist for mislabels, do not open modal
    if (query.type === Filters.MISLABELS && !mislabelsRange) {
      return false;
    }

    //default
    return true;
  };

  return (
    <div className={classNames(styles.row)} data-testid="query-panel-row">
      {logicText && (
        <div className={styles.logic} data-testid="query-panel-row-logic-text">
          {logicText}
        </div>
      )}

      {queries.map((query) => {
        const isRange = RANGE_FILTERS.includes(query.type);
        const isClickAllowed = interactive && checkIsClickAllowed(query);
        const labels = getTabLabels(query);
        const icon = getFilterIcon(query.type);
        const operator = getOperatorTitle(query.operator, query.type);
        const noColon = isSignOperator(operator);

        return (
          <QueryTab
            key={query.id}
            icon={icon}
            id={query.id}
            labels={labels}
            isRange={isRange}
            noColon={noColon}
            type={query.type}
            operator={operator}
            subType={query.subType}
            onClick={
              isClickAllowed
                ? (position: Position) => onClick(query, position)
                : undefined
            }
            onRemove={() => onRemove(query)}
            interactive={interactive}
          />
        );
      })}
      {interactive && (
        <div className={styles.actions}>
          {!hideAddFilter && <AddFilter rowRef={rowRef} />}
          {queries.length > 0 && (
            <>
              <div className={styles.separator}></div>
              {
                <div
                  data-testid="query-panel-row-reset-btn"
                  className={styles.resetBtn}
                  onClick={onReset}
                >
                  Reset
                </div>
              }
            </>
          )}
          {onAdvancedClick && (
            <>
              <div className={styles.separator}></div>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "exclude",
                      label: "Exclude",
                      onClick: onAdvancedClick,
                    },
                  ],
                }}
                placement="bottomRight"
                // trigger={"click"}
              >
                <Button
                  type={"link"}
                  // type="button"
                  className={styles.advancedBtn}
                  // style={{
                  //   background: "none",
                  //   border: "none",
                  //   color: "#1890ff",
                  //   cursor: "pointer",
                  //   textDecoration: "underline",
                  //   fontSize: "12px",
                  // }}
                >
                  Advanced
                </Button>
              </Dropdown>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const QueryPanel = ({ datasetId }: { datasetId: string }) => {
  const dispatch = useDispatch();
  const { vqlQueries, removeVqlQuery, setVqlQueries, vqlToQuery } =
    useVqlParams();

  const { imageDataMap } = useVisualSimilarityImages({ vqlQueries, datasetId });

  const handleRemoval = (query: Query) => {
    removeVqlQuery(query.id);
  };

  const handleRemoveAll = () => {
    setVqlQueries([]);
  };

  const handleSelection = (query: Query, position: Position) => {
    dispatch(
      setSelectedQueryModal({
        id: query.id,
        type: query.type,
        position: position,
      }),
    );
  };

  const queries: Query[] = useMemo(() => {
    return vqlQueries
      .map((vqlQuery) => vqlToQuery(vqlQuery, imageDataMap))
      .filter((query): query is Query => query !== null);
  }, [vqlQueries, vqlToQuery, imageDataMap]);

  const renderRows = () => {
    return (
      <div>
        <QueryPanelRow
          key="andRow"
          queries={queries}
          onRemove={handleRemoval}
          onClick={handleSelection}
          onReset={handleRemoveAll}
          dropZoneId="include-row"
        />
      </div>
    );
  };

  return (
    <div className={styles.row}>
      <div
        className={classNames(styles.queryPanel, {})}
        data-testid="query-panel"
      >
        {renderRows()}
      </div>
    </div>
  );
};

export default QueryPanel;
